'use client';

import * as React from 'react';

import { cn } from '../../lib/utils';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';

/**
 * "Are you sure?" dialog — the shared replacement for glp-frontend's
 * `react-confirm-alert` + `molecules/modals/ConfirmDialog`. Controlled: the
 * caller owns `open`. Confirm calls `onConfirm` and closes; Cancel, Escape
 * and the overlay only close.
 */
export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for deletes and removals. */
  destructive?: boolean;
  /** Disables the confirm button (e.g. while the request runs). */
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
  className?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  onConfirm,
  className,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('w-[calc(100vw-2rem)] max-w-md', className)}>
        <DialogHeader>
          <DialogTitle className='pr-6'>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogFooter className='mt-4 gap-2'>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            type='button'
            disabled={busy}
            className={cn(
              'rounded-[19px]',
              destructive && 'bg-red-600 text-white hover:bg-red-700',
            )}
            onClick={async () => {
              await onConfirm();
              onOpenChange(false);
            }}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
